/** @format */

import React from "react";
import { Link } from "react-router-dom";

import { ReactComponent as Tag } from "bootstrap-icons/icons/tag-fill.svg";
import { ReactComponent as Print } from "bootstrap-icons/icons/printer.svg";
import { ReactComponent as Pencil } from "bootstrap-icons/icons/pencil.svg";
import { ReactComponent as Duplicate } from "../../../assets/icons/duplicate.svg";
import { ReactComponent as BoxArrowUpRight } from "bootstrap-icons/icons/box-arrow-up-right.svg";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";

import SharePopup from "../../sharePopup/SharePopup";
import { useAppSelector } from "../../../app/hooks";
import { ResourceReference } from "../../../types";
import EditResourceRefModal from "../editUserResourceModal/EditResourceRefModal";
import ResourceLogs from "../resourceLog/ResourceLogs";
import "./ResourceFeatures.scss";

type Props = {
  resource: ResourceReference;
  authors: string[];
  fileIcon: (mimeType: string) => JSX.Element;
};

const ResourceFeatures: React.FC<Props> = ({ resource, authors, fileIcon }) => {
  const { t: _ } = useTranslation();
  const resourceObject = resource.Object;

  const isAuthenticated =
    useAppSelector((state) => state.auth.userToken?.token) || false;

  let filteredFiles = [...resourceObject.links];
  filteredFiles = filteredFiles.filter(
    (file) =>
      file.label === _("Open resource") &&
      (file.category === null || file.category === ""),
  );

  return (
    <div className="resource-features">
      {resource.Object.thumbnail && (
        <div className="mb-3 text-center">
          <figure className="resource-ref-thumbnail">
            <img
              className="rounded bg-secoundary border"
              src={resource.Object.thumbnail}
              alt={resource.Object.title}
            />
          </figure>
        </div>
      )}
      {isAuthenticated && (
        <>
          <EditResourceRefModal resourceRef={resource} />
        </>
      )}
      {resource.Status === "draft" && (
        <div
          role="status"
          aria-label="draft"
          className="card-badge draft d-flex w-100 mb-3 mt-2"
        >
          {_("draft")}
        </div>
      )}
      <div className="resource-actions w-100 d-flex justify-content-center gap-3 my-4">
        {resource?.Type === "QFF" && resource.Status === "draft" && (
          <>
            <Link
              role="link"
              title={_("Customise")}
              className="action customise"
              to={""}
            >
              <Pencil className="icon" />
            </Link>
            <Link
              role="link"
              title={_("Duplicate")}
              className="action duplicate"
              to={""}
            >
              <Duplicate className="icon" />
            </Link>
          </>
        )}
        {filteredFiles.length > 0 && (
          <Link
            role="link"
            title={_("Open")}
            className="action open"
            target="_blank"
            rel="noopener noreferrer"
            to={resourceObject.links[0].src}
          >
            <BoxArrowUpRight className="icon" />
          </Link>
        )}
        {resource?.Type === "BL" && filteredFiles.length > 0 && (
          <Button
            className="p-0 bg-transparent border-0"
            disabled={resource.Status === "draft"}
          >
            <Link
              role="link"
              title={_("Print")}
              className="action print"
              target="_blank"
              rel="noopener noreferrer"
              to={resourceObject.links[0].src}
            >
              <Print className="icon" />
            </Link>
          </Button>
        )}
        {resourceObject.links?.[0]?.src && (
          <div className="d-flex">
            <SharePopup resource={resource} />
          </div>
        )}
      </div>
      <div className="mb-3 d-flex justify-content-center gap-3 authors">
        <strong>{_("Authors", { count: authors.length })}: </strong>
        <div className="d-flex flex-wrap">
          {authors[0].length
            ? resourceObject.authors
            : (
              _("No authors provided")
            )
          }
        </div>
      </div>

      <div
        className="
        acknowledgements 
        mb-3 
        d-flex 
        flex-column
        flex-sm-row
        align-items-center
        align-items-sm-start 
        justify-content-center 
        gap-1"
      >
        <strong>{_("Acknowledgements")}:</strong>

        {resourceObject.acknowledgements.length === 0 ? (
          <div className="d-flex flex-wrap">
            {_("No acknowledgements provided")}
          </div>
        ) : (
          <ul className="list-unstyled mb-0">
            {resourceObject.acknowledgements.length > 0 &&
            Array.isArray(resourceObject.acknowledgements) ? (
              resourceObject.acknowledgements.map((file) => (
                <li key={file.src + file.label}>
                  <a
                    title={file.label}
                    className="btn btn-light py-0"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={file.src}
                    download
                  >
                    {fileIcon(file.mimeType)} {file.label}{" "}
                    <small className="fs-6 text-muted">
                      <BoxArrowUpRight />
                    </small>
                  </a>
                </li>
              ))
            ) : (
              <li>
                <a
                  title={_("Acknowledgements")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-light py-0"
                  href={resourceObject.acknowledgements.toString()}
                  download
                >
                  {fileIcon("pdf")} {_("Acknowledgements")}{" "}
                  <small className="fs-6 text-muted">
                    <BoxArrowUpRight />
                  </small>
                </a>
              </li>
            )}
          </ul>
        )}
      </div>

      <div className="tags mb-3 d-flex justify-content-center gap-3">
        <strong>{_("Tags")}:</strong>
        <div className="d-flex flex-wrap">
          {resourceObject.tags.length > 0
            ? resourceObject.tags.map(
                (_tag: string, index: number) =>
                  _tag && (
                    <Link
                      role="link"
                      title={_tag}
                      key={_tag + index}
                      className="btn rounded-pill p-0 me-2 text-decoration-none btn-link"
                      to={`../?tags=${_tag}`}
                    >
                      <Tag />
                      &thinsp;{_tag}
                    </Link>
                  ),
              )
            : _("No tags provided")}
        </div>
      </div>

      <ResourceLogs resource={resource} />
    </div>
  );
};

export default ResourceFeatures;
